import clsx from "clsx";
import { FunctionComponent, useEffect, useState } from "react";

import CloseIcon from "@ui/components/icon/CloseIcon";
import { PMC_LEGAL_URLS } from "@ui/components/navigation/Footer/data/pmcLegalUrls";
import SELECTORS from "@ui/config/class-selectors.config";
import { useAnalytics } from "@voxmedia/duet-context/context/analytics-context";
import {
  getStorageValue,
  setStorageValue,
} from "@voxmedia/duet-utils/web-storage";

import * as styles from "./PmcPrivacyBanner.css";
import { PmcPrivacyChoicesLink } from "./PmcPrivacyChoicesLink";

export const PMC_PRIVACY_BANNER_STORAGE_KEY = "duet-pmc-privacy-banner";
export const PMC_PRIVACY_BANNER_ACCEPTED_VALUE = "accepted";

const HEADING_ID = "pmc-privacy-banner-heading";
const BODY_ID = "pmc-privacy-banner-body";

const PmcPrivacyBanner: FunctionComponent = () => {
  const analytics = useAnalytics();
  const [visible, setVisible] = useState<boolean | null>(null);

  useEffect(() => {
    setVisible(
      getStorageValue(PMC_PRIVACY_BANNER_STORAGE_KEY) !==
        PMC_PRIVACY_BANNER_ACCEPTED_VALUE,
    );
  }, []);

  const acceptBanner = (name: string) => {
    setStorageValue(
      PMC_PRIVACY_BANNER_STORAGE_KEY,
      PMC_PRIVACY_BANNER_ACCEPTED_VALUE,
    );
    setVisible(false);
    analytics?.emit({
      event: "footer_click",
      footer_click: { name },
    });
  };

  const handleDismiss = () => {
    acceptBanner("PMC Privacy Banner Dismiss");
  };

  if (!visible) {
    return null;
  }

  return (
    <aside
      className={clsx(SELECTORS.PmcPrivacyBanner, styles.banner)}
      role="dialog"
      aria-modal="false"
      aria-labelledby={HEADING_ID}
      aria-describedby={BODY_ID}
    >
      <div className={styles.inner}>
        <button
          type="button"
          className={styles.closeButton}
          onClick={handleDismiss}
          aria-label="Dismiss privacy notice"
        >
          <CloseIcon className={styles.closeIcon} fill="currentColor" />
        </button>
        <p className={styles.heading} id={HEADING_ID}>
          Terms of Use/Your Privacy Rights
        </p>
        <p className={styles.body} id={BODY_ID}>
          This website is now part of PMX Global, LLC, a subsidiary of Penske
          Media Corporation. By continuing to use our services, you agree to the
          PMC{" "}
          <a
            className={styles.link}
            href={PMC_LEGAL_URLS.termsOfUse}
            rel="nofollow"
          >
            Terms of Use
          </a>
          , including waiver and arbitration. PMC, its service providers, and
          third-party partners process information about you and how you use our
          services, including clicks and screen recordings, using first and
          third-party cookies, pixels, and similar technologies. Where
          applicable, you can opt out of certain uses of cookies via the{" "}
          <PmcPrivacyChoicesLink
            className={styles.privacyChoicesLink}
            iconClassName={styles.privacyChoicesIcon}
            showIcon
          />{" "}
          link in our footer. Learn more about our data practices in our{" "}
          <a
            className={styles.link}
            href={PMC_LEGAL_URLS.privacyPolicy}
            rel="nofollow"
          >
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </aside>
  );
};

export default PmcPrivacyBanner;
